'use client';

import { c as _c } from "react/compiler-runtime";
import { Icon } from '~/components/primitives';
import { useI18n } from '~/features/i18n';
import useCartState from './useCartState';
import useWishlistState from '~/features/wishlist/useWishlistState';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import Popover from '~/components/primitives/Popover';
import { cn } from '~/utils';
import { CACHED_PARTICIPANTS_FILTER } from '~/features/activities/search/client/context';
import { usePortalHREFs } from '~/features/global/portal';
import { HeaderHovercardItem, HeaderHovercardItemImage } from './ui/header-hovercard';
import { ActivityItemCard } from './ui/activity-item-card';
import { Badge } from '~/components/ui/badge';
import { Tooltip } from '~/components/primitives';
import { usePortalCtx } from '~/features/global/portal';
import { useLocalStorageState } from '~/modules/browser/storage';
import { CartItemLocationLine } from './CartItemLocationLine';
export type CartItemType = NonNullable<TRPC_ROUTER.Outputs['cart']['get']>['items'][number];
function useCartItemData(item, participants) {
  const $ = _c(9);
  const i18n = useI18n();
  const hrefs = usePortalHREFs();
  const participantsStorage = useLocalStorageState(CACHED_PARTICIPANTS_FILTER);
  const featuredImage = item.activity?.media?.header?.path ?? null;
  const slug = item.activity?.group?.slug;
  const selectedParticipants = typeof participants === "number" && participants > 0 ? participants : participantsStorage.state;
  let t0;
  if ($[0] !== hrefs || $[1] !== item.activity || $[2] !== selectedParticipants || $[3] !== slug) {
    t0 = slug && typeof slug === "string" ? hrefs.PDP({
      slug,
      id: item.activity.id,
      participants: selectedParticipants
    }) : null;
    $[0] = hrefs;
    $[1] = item.activity;
    $[2] = selectedParticipants;
    $[3] = slug;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const activityUrl = t0;
  let t1;
  if ($[5] !== activityUrl || $[6] !== featuredImage || $[7] !== i18n) {
    t1 = {
      featuredImage,
      activityUrl,
      i18n
    };
    $[5] = activityUrl;
    $[6] = featuredImage;
    $[7] = i18n;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}
const CartItemTitle: React.FC<{
  href: string | null;
  name: string;
  size: 'sm' | 'md';
}> = props => {
  const $ = _c(12);
  if (props.href) {
    const t0 = props.href;
    const t1 = props.size === "sm" ? "block truncate text-xs font-bold text-primary" : "text-sm leading-tight text-primary sm:text-base";
    let t2;
    if ($[0] !== t1) {
      t2 = cn("hover:text-orange-500", t1);
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== props.name || $[3] !== props.size) {
      t3 = props.size === "sm" ? props.name : <strong>{props.name}</strong>;
      $[2] = props.name;
      $[3] = props.size;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== props.href || $[6] !== t2 || $[7] !== t3) {
      t4 = <a href={t0} className={t2}>{t3}</a>;
      $[5] = props.href;
      $[6] = t2;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t0;
  if ($[9] !== props.name || $[10] !== props.size) {
    t0 = props.size === "sm" ? <p className="truncate text-xs font-bold text-primary">{props.name}</p> : <p className="text-sm leading-tight text-primary sm:text-base"><strong>{props.name}</strong></p>;
    $[9] = props.name;
    $[10] = props.size;
    $[11] = t0;
  } else {
    t0 = $[11];
  }
  return t0;
};
const CartItemDeleteButton: React.FC<{
  item: CartItemType;
  size: 'sm' | 'md';
}> = props => {
  const i18n = useI18n();
  const deleteCartItem = useCartState().useDeleteCartItemMutation({
    optimistic: props.size === 'sm',
    silentSuccess: props.size === 'sm'
  });
  const wishlist = useWishlistState();
  const label = i18n._('cart.item.delete', undefined, {
    message: 'Remove from cart'
  });
  const actionsLabel = i18n._('cart.item.actions', undefined, {
    message: 'Open item actions'
  });
  if (props.size === 'sm') {
    return <button type="button" data-testid="cart-item-remove" className="shrink-0 p-1 text-blue-400 transition-colors hover:text-orange-500" aria-label={label} disabled={deleteCartItem.isPending} onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      deleteCartItem.mutate({
        id: Number(props.item.id)
      });
    }}>
                <Icon name="trash" size="xs" />
            </button>;
  }
  async function handleDelete(close: () => void) {
    close();
    await deleteCartItem.mutateAsync({
      id: Number(props.item.id)
    });
  }
  async function handleMoveToWishlist(close_0: () => void) {
    close_0();
    await deleteCartItem.mutateAsync({
      id: Number(props.item.id)
    });
    if (props.item.activity) {
      wishlist.upsert({
        activityId: props.item.activity.id,
        name: props.item.activity.name ?? '',
        slug: props.item.activity.group?.slug ?? '',
        imagePath: props.item.activity.media?.header?.path ?? null,
        variantId: props.item.activityVariantId ?? null,
        languageId: props.item.activityLanguageId ?? null
      }, {
        silent: true
      });
    }
  }
  return <Popover position="bottom-end" content={({
    close: close_1
  }) => <div className="flex min-w-44 flex-col rounded-xl border border-blue-100 bg-white py-1 shadow-md">
                    <button type="button" data-testid="cart-item-delete-confirm" className="flex items-center gap-2 px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-blue-50" onClick={() => handleDelete(close_1)}>
                        <Icon name="trash" size="xs" />
                        {i18n._('cart.item.delete-confirm', undefined, {
        message: 'Delete'
      })}
                    </button>
                    <button type="button" data-testid="cart-item-move-to-wishlist" className="flex items-center gap-2 px-3 py-2 text-left text-sm text-primary transition-colors hover:bg-blue-50" onClick={() => handleMoveToWishlist(close_1)}>
                        <Icon name="heart" size="xs" />
                        {i18n._('cart.item.move-to-wishlist', undefined, {
        message: 'Move to wishlist'
      })}
                    </button>
                </div>}>
            <button type="button" data-testid="cart-item-actions-menu" className="shrink-0 rounded-full p-1.5 text-blue-400 transition-colors hover:bg-blue-50 hover:text-orange-500 sm:p-2" aria-label={actionsLabel}>
                <Icon name="trash" size="sm" />
            </button>
        </Popover>;
};

/**
 * Reusable cart item card component.
 * - `sm`: compact version used in header dropdown
 * - `md`: full version used on the cart page
 */
export const CartItemCard: React.FC<{
  item: CartItemType;
  size: 'sm' | 'md';
  participants?: number;
  readonly?: boolean;
  /** Show the chosen location under the title (checkout summaries; off in the nav preview). */
  showLocation?: boolean;
  children?: React.ReactNode;
}> = props => {
  const $ = _c(47);
  const cartItemData = useCartItemData(props.item, props.participants);
  const portal = usePortalCtx();
  const name = props.item.activity?.name ?? "";
  const variantName = props.item.activityVariant?.name;
  const showVariant = variantName && name !== variantName;
  let t0;
  if ($[0] !== portal.bizz || $[1] !== portal.portal || $[2] !== props.item.activityId) {
    t0 = props.item.activityId !== null && portal.portal === "BIZZ" && portal.bizz.excludedActivityIDs.includes(props.item.activityId);
    $[0] = portal.bizz;
    $[1] = portal.portal;
    $[2] = props.item.activityId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const isUnavailable = t0;
  const bizzName = portal.portal === "BIZZ" ? portal.bizz.name : "";
  const nomodalHref = cartItemData.activityUrl ? `${cartItemData.activityUrl}&nomodal` : null;
  if (props.size === "sm") {
    const t1 = isUnavailable ? "opacity-50 grayscale" : undefined;
    let t2;
    if ($[4] !== cartItemData.featuredImage || $[5] !== name || $[6] !== t1) {
      t2 = <HeaderHovercardItemImage src={cartItemData.featuredImage} alt={name} className={t1} />;
      $[4] = cartItemData.featuredImage;
      $[5] = name;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== name) {
      t3 = <CartItemTitle href={null} name={name} size="sm" />;
      $[8] = name;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    let t4;
    if ($[10] !== bizzName || $[11] !== cartItemData.i18n || $[12] !== isUnavailable || $[13] !== showVariant || $[14] !== variantName) {
      t4 = isUnavailable ? <p className="truncate text-[11px] text-orange-500">{cartItemData.i18n._("wishlist.item.unavailable-for-bizz_{bizzName}", {
          bizzName
        }, {
          message: "This activity is not available for {bizzName}"
        })}</p> : showVariant ? <p className="truncate text-xs text-blue-600">{variantName}</p> : null;
      $[10] = bizzName;
      $[11] = cartItemData.i18n;
      $[12] = isUnavailable;
      $[13] = showVariant;
      $[14] = variantName;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    let t5;
    if ($[16] !== props.item || $[17] !== props.showLocation) {
      t5 = props.showLocation && <CartItemLocationLine item={props.item} className="flex items-center gap-1 text-xs text-blue-600" />;
      $[16] = props.item;
      $[17] = props.showLocation;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    let t6;
    if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
      t6 = <div className="min-w-0 flex-1">{t3}{t4}{t5}</div>;
      $[19] = t3;
      $[20] = t4;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    let t7;
    if ($[23] !== props.item || $[24] !== props.readonly) {
      t7 = !props.readonly && <CartItemDeleteButton item={props.item} size="sm" />;
      $[23] = props.item;
      $[24] = props.readonly;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== nomodalHref || $[27] !== t2 || $[28] !== t6 || $[29] !== t7) {
      t8 = <li><HeaderHovercardItem href={nomodalHref}>{t2}{t6}{t7}</HeaderHovercardItem></li>;
      $[26] = nomodalHref;
      $[27] = t2;
      $[28] = t6;
      $[29] = t7;
      $[30] = t8;
    } else {
      t8 = $[30];
    }
    return t8;
  }
  let t1;
  if ($[31] !== name || $[32] !== nomodalHref) {
    t1 = <CartItemTitle href={nomodalHref} name={name} size="md" />;
    $[31] = name;
    $[32] = nomodalHref;
    $[33] = t1;
  } else {
    t1 = $[33];
  }
  let t2;
  if ($[34] !== bizzName || $[35] !== cartItemData.i18n || $[36] !== isUnavailable || $[37] !== props.item || $[38] !== props.readonly) {
    t2 = !props.readonly ? isUnavailable ? <div className="flex items-center gap-1.5"><Tooltip content={cartItemData.i18n._("wishlist.item.unavailable-for-bizz_{bizzName}", {
        bizzName
      }, {
        message: "This activity is not available for {bizzName}"
      })}><Badge variant="destructive">{cartItemData.i18n._("wishlist.item.not-available", undefined, {
            message: "Not available"
          })}</Badge></Tooltip><CartItemDeleteButton item={props.item} size="md" /></div> : <CartItemDeleteButton item={props.item} size="md" /> : undefined;
    $[34] = bizzName;
    $[35] = cartItemData.i18n;
    $[36] = isUnavailable;
    $[37] = props.item;
    $[38] = props.readonly;
    $[39] = t2;
  } else {
    t2 = $[39];
  }
  let t3;
  if ($[40] !== cartItemData.featuredImage || $[41] !== name || $[42] !== nomodalHref || $[43] !== props.children || $[44] !== t1 || $[45] !== t2) {
    t3 = <ActivityItemCard listAs="div" imageSrc={cartItemData.featuredImage} imageAlt={name} imageHref={nomodalHref} imageClassName="bg-gray-100" title={t1} topRightAction={t2}>{props.children}</ActivityItemCard>;
    $[40] = cartItemData.featuredImage;
    $[41] = name;
    $[42] = nomodalHref;
    $[43] = props.children;
    $[44] = t1;
    $[45] = t2;
    $[46] = t3;
  } else {
    t3 = $[46];
  }
  return t3;
};